import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Trash2, Check, X, Pin, PinOff, RefreshCw, Pencil } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "../../../lib/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const badge = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

export default function TestimonialsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | pending | approved | rejected
  const [editing, setEditing] = useState(null); // testimonial object or null
  const [form, setForm] = useState({ name: "", location: "", quote: "", rating: 5, avatar_url: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/testimonials");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const counts = items.reduce((acc, t) => { acc[t.status] = (acc[t.status] || 0) + 1; return acc; }, {});
  const filtered = filter === "all" ? items : items.filter((t) => t.status === filter);

  const setStatus = async (id, status) => {
    try {
      await adminApi.put(`/admin/testimonials/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const setFeatured = async (id, is_featured) => {
    try {
      await adminApi.put(`/admin/testimonials/${id}`, { is_featured });
      toast.success(is_featured ? "Pinned to top" : "Unpinned");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try {
      await adminApi.delete(`/admin/testimonials/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name || "",
      location: t.location || "",
      quote: t.quote || "",
      rating: t.rating || 5,
      avatar_url: t.avatar_url || "",
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await adminApi.put(`/admin/testimonials/${editing.id}`, {
        name: form.name,
        location: form.location || null,
        quote: form.quote,
        rating: Number(form.rating),
        avatar_url: form.avatar_url || null,
      });
      toast.success("Updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="testimonials-manager-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h3 className="font-display text-lg font-semibold text-[#0B192C]">
          Testimonials ({items.length}) <span className="text-sm font-normal text-slate-500">— {counts.pending || 0} pending · {counts.approved || 0} approved</span>
        </h3>
        <div className="flex gap-2">
          {["all", "pending", "approved", "rejected"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`testimonial-filter-${f}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize transition-colors ${filter === f ? "bg-[#0B192C] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#0B192C]"}`}
            >
              {f}{f !== "all" ? ` (${counts[f] || 0})` : ""}
            </button>
          ))}
          <Button variant="outline" onClick={load} className="rounded-full h-9 px-4 text-sm" data-testid="testimonials-refresh-button">
            <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 text-sm text-slate-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 text-sm text-slate-500" data-testid="testimonials-empty-admin">No testimonials in this view.</div>
        ) : filtered.map((t) => (
          <div key={t.id} className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col sm:flex-row gap-4" data-testid={`testimonial-row-${t.id}`}>
            <div className="flex-shrink-0">
              {t.avatar_url ? (
                <img src={t.avatar_url} alt={t.name} className="w-12 h-12 rounded-full object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-orange-50 text-[#FF6B00] font-display font-bold flex items-center justify-center">{(t.name || "?")[0].toUpperCase()}</div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-display font-semibold text-[#0B192C]">{t.name}</span>
                {t.location && <span className="text-xs text-slate-500">· {t.location}</span>}
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold ${badge[t.status] || badge.pending}`}>{t.status}</span>
                {t.is_featured && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 border border-orange-200 text-[#E65C00] text-xs font-semibold"><Pin size={10} /> Featured</span>}
                <span className="inline-flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} size={12} className={i <= (t.rating || 0) ? "fill-[#FF6B00] text-[#FF6B00]" : "text-slate-300"} />
                  ))}
                </span>
              </div>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">{t.quote}</p>
              <div className="mt-2 text-xs text-slate-400">{new Date(t.created_at).toLocaleString()}{t.email ? ` · ${t.email}` : ""}</div>

              <div className="mt-3 flex flex-wrap gap-2">
                {t.status !== "approved" && (
                  <button onClick={() => setStatus(t.id, "approved")} data-testid={`testimonial-approve-${t.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-semibold">
                    <Check size={12} /> Approve
                  </button>
                )}
                {t.status !== "rejected" && (
                  <button onClick={() => setStatus(t.id, "rejected")} data-testid={`testimonial-reject-${t.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-50 text-red-700 hover:bg-red-100 text-xs font-semibold">
                    <X size={12} /> Reject
                  </button>
                )}
                {t.status !== "pending" && (
                  <button onClick={() => setStatus(t.id, "pending")} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200 text-xs font-semibold">
                    Reset to pending
                  </button>
                )}
                <button onClick={() => setFeatured(t.id, !t.is_featured)} data-testid={`testimonial-feature-${t.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-orange-50 text-[#E65C00] hover:bg-orange-100 text-xs font-semibold">
                  {t.is_featured ? <><PinOff size={12} /> Unpin</> : <><Pin size={12} /> Pin</>}
                </button>
                <button onClick={() => openEdit(t)} data-testid={`testimonial-edit-${t.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-50 text-slate-700 hover:bg-slate-100 text-xs font-semibold">
                  <Pencil size={12} /> Edit
                </button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`testimonial-delete-${t.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-slate-500 hover:text-red-600 hover:bg-red-50 text-xs font-semibold">
                      <Trash2 size={12} /> Delete
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this review?</AlertDialogTitle>
                      <AlertDialogDescription>{t.name}'s testimonial will be permanently removed.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(t.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="testimonial-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit testimonial</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-10 rounded-lg" />
              </div>
              <div>
                <Label>Location</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1.5 h-10 rounded-lg" />
              </div>
            </div>
            <div>
              <Label>Quote</Label>
              <Textarea rows={5} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} className="mt-1.5 rounded-lg" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Rating (1-5)</Label>
                <Input type="number" min="1" max="5" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} className="mt-1.5 h-10 rounded-lg" />
              </div>
              <div>
                <Label>Avatar URL (optional)</Label>
                <Input value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} placeholder="https://…" className="mt-1.5 h-10 rounded-lg" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-full">Cancel</Button>
            <Button onClick={saveEdit} disabled={saving} className="rounded-full bg-[#FF6B00] hover:bg-[#E65C00] font-semibold">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
